import { ReloadOutlined } from '@ant-design/icons';
import { ResponsiveLine } from '@nivo/line';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Grid,
  Row,
  Space,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BLACK, BLUE1, GREEN1, ORANGE1, RED1 } from '../../../constants/color';
import {
  DATE_FORMAT,
  DEFAULT_FONT_SIZE,
  GENERAL_TIMEOUT,
  MEDIUM_FONT_SIZE,
} from '../../../constants/systemConstants';
import { GoodsDetailData, NivoLineChartData } from '../../../types';
import { alertMessage } from '../../../utils/alertMessage';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import { addCommas, getPercent } from '../../../utils/helperFunction';
import {
  getLineGraphBottomAxisLabel,
  nivoToolTip,
} from '../../../utils/nivoGraphTools';
import FiveHundred from '../../FiveHundred';
import FourZeroThree from '../../FourZeroThree';
import SellersDropdown from '../../sellers/SellersDropdown';
import TableToolbar from '../../table/TableToolbar';

type GoodsDetailProps = {
  isSeller: boolean;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

const GoodsDetail = ({
  isSeller,
  isLoading,
  setIsLoading,
}: GoodsDetailProps) => {
  //General Components
  const [fourZeroThree, setFourZeroThree] = useState<boolean>(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const screens = Grid.useBreakpoint();
  const { RangePicker } = DatePicker;
  const isSubscribed = useRef(true);
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [numOfDays, setNumOfDays] = useState<Number>(7);
  //This Year range
  const [range1Data, setRange1Data] = useState<GoodsDetailData[]>([]);
  //Last Year range
  const [range2Data, setRange2Data] = useState<GoodsDetailData[]>([]);
  const [lineGraphData, setLineGraphData] = useState<NivoLineChartData[]>([]);
  const [goodsDetailTable, setGoodsDetailTable] = useState<
    {
      date1: string;
      newGoodsNum1: number;
      date2: string;
      newGoodsNum2: number;
      newGoodsNumGrowth: string;
    }[]
  >([]);
  //Text/Visuals Components
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const columnKeys = [
    'date1',
    'newGoodsNum1',
    'date2',
    'newGoodsNum2',
    'newGoodsNumGrowth',
  ];

  const getGrowthAndLineData = useCallback(() => {
    let goodsDetailTable: {
      date1: string;
      newGoodsNum1: number;
      date2: string;
      newGoodsNum2: number;
      newGoodsNumGrowth: string;
    }[] = [];
    let lineGraph: NivoLineChartData[] = [];
    let newGoodsNum1Data: { x?: string; y: string | number }[] = [];
    let newGoodsNum2Data: { x?: string; y: string | number }[] = [];
    if (
      range1Data &&
      range2Data &&
      range1Data.length === range2Data.length &&
      range1Data.length > 0 &&
      range2Data.length > 0
    ) {
      for (let i = 0; i < range1Data.length; i++) {
        newGoodsNum1Data.push({
          x: range1Data[i].date,
          y: range1Data[i].newGoodsNum,
        });
        newGoodsNum2Data.push({
          x: range1Data[i].date,
          y: range2Data[i].newGoodsNum,
        });
        goodsDetailTable.push({
          date1: range1Data[i].date,
          newGoodsNum1: range1Data[i].newGoodsNum,
          date2: range2Data[i].date,
          newGoodsNum2: range2Data[i].newGoodsNum,
          newGoodsNumGrowth: getPercent(
            range2Data[i].newGoodsNum,
            range1Data[i].newGoodsNum
          ),
        });
      }
      lineGraph.push({
        id: `${t('analysis.goodsDetail.newGoodsNum')}`,
        color: BLUE1,
        data: newGoodsNum1Data,
      });
      lineGraph.push({
        id: `${t('analysis.goodsDetail.newGoodsNum')} (${t(
          'analysis.goodsDetail.lastYear'
        )})`,
        color: ORANGE1,
        data: newGoodsNum2Data,
      });
      setLineGraphData(lineGraph);
      setGoodsDetailTable(goodsDetailTable);
    }
  }, [range1Data, range2Data, t]);

  useEffect(() => {
    getGrowthAndLineData();
  }, [getGrowthAndLineData]);

  /**
   * Grabs all the needed data for Order Analysis
   */
  const getData = useCallback(
    (date?: { key: string; start: string; end: string }) => {
      if (isSubscribed.current) setIsLoading(true);
      getDataWithAuthToken('analysis/goods/detail', {
        params: {
          isSeller: getDashboardStyle().isSellerSwitch ? isSeller : undefined,
          sellerId:
            getDashboardStyle().isSellerSwitch &&
            isSeller &&
            formRef.current &&
            form.getFieldValue('sellerId')
              ? form.getFieldValue('sellerId')
              : undefined,
          startDate: date ? date.start : undefined,
          endDate: date ? date.end : undefined,
        },
      })
        .then((response) => {
          if (response && response.goodStatus) {
            if (isSubscribed.current) {
              if (date) {
                if (date.key === 'range1') {
                  setRange1Data(response.data.list);
                } else if (date.key === 'range2') {
                  setRange2Data(response.data.list);
                }
              }
            }
          } else if (response && response.returnCode === 403) {
            if (isSubscribed.current) setFourZeroThree(true);
          } else {
            setFiveHundred(true);
            alertMessage(
              'error',
              response?.msg || t('general.noResponse'),
              response?.data || undefined
            );
          }
          if (isSubscribed.current) setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          if (isSubscribed.current) setIsLoading(false);
        });
    },
    [t, setFourZeroThree, setFiveHundred, isSeller, form, setIsLoading]
  );

  useEffect(() => {
    let start = moment().subtract(8, 'days');
    let end = moment().subtract(1, 'days');
    getData({
      key: 'range1',
      start: start.format(DATE_FORMAT),
      end: end.format(DATE_FORMAT),
    });
    getData({
      key: 'range2',
      start: start.subtract(1, 'years').format(DATE_FORMAT),
      end: end.subtract(1, 'years').format(DATE_FORMAT),
    });
  }, [getData]);

  const columns: ColumnsType<{
    date1: string;
    newGoodsNum1: number;
    date2: string;
    newGoodsNum2: number;
    newGoodsNumGrowth: string;
  }> = [
    {
      title: `${t('analysis.goodsDetail.date')} (${t(
        'analysis.goodsDetail.lastYear'
      )})`,
      fixed: screens.lg ? 'left' : undefined,
      key: 'date2',
      dataIndex: 'date2',
      width: 120,
      render: (text: string) => text,
    },
    {
      title: t('analysis.goodsDetail.newGoodsNum'),
      dataIndex: 'newGoodsNum2',
      key: 'newGoodsNum2',
      width: 150,
      render: (text: string) => addCommas(text),
    },
    {
      title: t('analysis.goodsDetail.date'),
      dataIndex: 'date1',
      key: 'date1',
      width: 100,
      render: (text: string) => text,
    },
    {
      title: t('analysis.goodsDetail.newGoodsNum'),
      dataIndex: 'newGoodsNum1',
      key: 'newGoodsNum1',
      width: 150,
      render: (text: string) => addCommas(text),
    },
    {
      title: t('analysis.goodsDetail.newGoodsNumGrowth'),
      dataIndex: 'newGoodsNumGrowth',
      key: 'newGoodsNumGrowth',
      width: 200,
      render: (text: string) => (
        <div
          style={{
            color:
              text[0] === '-' ? RED1 : text[0] !== undefined ? GREEN1 : BLACK,
          }}
        >
          {text ? `${addCommas(text)}%` : `0%`}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!isSeller) form.resetFields(['sellerId']);
  }, [isSeller, form]);

  return (
    <>
      {fourZeroThree ? (
        <Card>
          <FourZeroThree />
        </Card>
      ) : fiveHundred ? (
        <Card>
          <FiveHundred />
        </Card>
      ) : (
        <Spin style={{ width: '100%' }} spinning={isLoading}>
          <Form form={form} ref={formRef} style={{ paddingBottom: 20 }}>
            <Row gutter={[16, 0]}>
              <Col span={24} md={24} lg={24} xl={8}>
                <Form.Item
                  name={'date'}
                  label={t('nivo.date')}
                  initialValue={[
                    moment().subtract(8, 'days'),
                    moment().subtract(1, 'days'),
                  ]}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    format={DATE_FORMAT}
                    placeholder={[t('nivo.startDate'), t('nivo.endDate')]}
                    ranges={{
                      [`${t('nivo.week')}`]: [
                        moment().startOf('isoWeek'),
                        moment().endOf('isoWeek'),
                      ],
                      [`${t('nivo.month')}`]: [
                        moment().startOf('month'),
                        moment().endOf('month'),
                      ],
                      [`${t('nivo.firstHalfYear')}`]: [
                        moment().startOf('year'),
                        moment().startOf('year').add(6, 'months'),
                      ],
                      [`${t('nivo.secondHalfYear')}`]: [
                        moment().startOf('year').add(6, 'months'),
                        moment().endOf('year'),
                      ],
                      [`${t('nivo.year')}`]: [
                        moment().startOf('year'),
                        moment().endOf('year'),
                      ],
                    }}
                    onChange={(value) => {
                      if (value && value[0] && value[1]) {
                        setNumOfDays(value[1].diff(value[0], 'days') + 1);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              {isSeller && (
                <Col span={24} md={24} lg={24} xl={8}>
                  <Form.Item
                    name="sellerId"
                    label={t('analysis.goodsSales.seller')}
                  >
                    <SellersDropdown />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Space align="start">
                  <Button
                    htmlType="submit"
                    type="primary"
                    onClick={() => {
                      setRange1Data([]);
                      setRange2Data([]);
                      setGoodsDetailTable([]);
                      if (typingTimeout) clearTimeout(typingTimeout);
                      form.getFieldValue('date') &&
                        setTypingTimeout(
                          setTimeout(() => {
                            let start = moment(form.getFieldValue('date')[0]);
                            let end = moment(form.getFieldValue('date')[1]);
                            getData({
                              key: 'range1',
                              start: start.format(DATE_FORMAT),
                              end: end.format(DATE_FORMAT),
                            });
                            getData({
                              key: 'range2',
                              start: start
                                .subtract(1, 'years')
                                .format(DATE_FORMAT),
                              end: end.subtract(1, 'years').format(DATE_FORMAT),
                            });
                          }, GENERAL_TIMEOUT)
                        );
                    }}
                  >
                    {t('analysis.goodsDetail.ok')}
                  </Button>
                  <Button
                    onClick={() => {
                      setRange1Data([]);
                      setRange2Data([]);
                      form.resetFields();
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          let start = moment().subtract(8, 'days');
                          let end = moment().subtract(1, 'days');
                          getData({
                            key: 'range1',
                            start: start.format(DATE_FORMAT),
                            end: end.format(DATE_FORMAT),
                          });
                          getData({
                            key: 'range2',
                            start: start
                              .subtract(1, 'years')
                              .format(DATE_FORMAT),
                            end: end.subtract(1, 'years').format(DATE_FORMAT),
                          });
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('analysis.goodsDetail.reset')}
                  </Button>
                </Space>
                <Tooltip title={t('general.refresh')}>
                  <Button
                    type="text"
                    onClick={() => {
                      if (form.getFieldValue('date')) {
                        setTypingTimeout(
                          setTimeout(() => {
                            let start = moment(form.getFieldValue('date')[0]);
                            let end = moment(form.getFieldValue('date')[1]);
                            getData({
                              key: 'range1',
                              start: start.format(DATE_FORMAT),
                              end: end.format(DATE_FORMAT),
                            });
                            getData({
                              key: 'range2',
                              start: start
                                .subtract(1, 'years')
                                .format(DATE_FORMAT),
                              end: end.subtract(1, 'years').format(DATE_FORMAT),
                            });
                          }, GENERAL_TIMEOUT)
                        );
                      }
                    }}
                    icon={<ReloadOutlined />}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Form>

          {lineGraphData.length > 0 && (
            <Col span={24} style={{ paddingBottom: 20 }}>
              <div /**Line Graph Title */
                style={{ textAlign: 'center', fontSize: MEDIUM_FONT_SIZE }}
              >
                {t('analysis.goodsDetail.newGoodsNum')}
              </div>
              <div style={{ width: '100%', height: 550 }} /**Line Graph */>
                {
                  <ResponsiveLine
                    theme={{
                      fontSize: DEFAULT_FONT_SIZE,
                      axis: {
                        legend: { text: { fontSize: DEFAULT_FONT_SIZE } },
                      },
                    }}
                    colors={(d) => d.color}
                    tooltip={(point) =>
                      nivoToolTip({
                        ...point,
                        xAxisLength: lineGraphData[0].data.length,
                        tooltipType: 'Standard',
                      })
                    }
                    xScale={{
                      type: 'time',
                      format: '%Y-%m-%d',
                      useUTC: false,
                    }}
                    yScale={{
                      type: 'linear',
                      min: 'auto',
                      max: 'auto',
                      stacked: false,
                      reverse: false,
                    }}
                    useMesh
                    animate={true}
                    enableGridX={false}
                    data={lineGraphData}
                    axisBottom={getLineGraphBottomAxisLabel(numOfDays)}
                    axisLeft={{
                      legend: t('nivo.number'),
                      legendOffset: -60,
                      legendPosition: 'middle',
                    }}
                    margin={{
                      top: 10,
                      right: 20,
                      bottom: 60,
                      left: 70,
                    }}
                  />
                }
              </div>
            </Col>
          )}
          {goodsDetailTable && goodsDetailTable.length > 0 /**Table */ && (
            <Col span={24}>
              <TableToolbar
                columns={columns}
                columnKeys={columnKeys}
                rows={goodsDetailTable.map((goodsDetail) => ({
                  ...goodsDetail,
                  newGoodsNumGrowth:
                    goodsDetail.newGoodsNumGrowth &&
                    goodsDetail.newGoodsNumGrowth !== '0'
                      ? `${addCommas(goodsDetail.newGoodsNumGrowth)}%`
                      : '0%',
                }))}
                exportConfig={{
                  fileName: 'GOODS_DETAIL_ANALYSIS',
                }}
              />
              <Table
                size="small"
                dataSource={goodsDetailTable}
                columns={columns}
                scroll={{ y: 500, x: 1200 }}
                rowKey={(record) => (record.date1 ? record.date1 : '')}
                pagination={{
                  pageSize: 50,
                  showTotal: (total, range) =>
                    t('general.paginationTotal', {
                      start: range[0],
                      end: range[1],
                      total: total,
                    }),
                  hideOnSinglePage: true,
                }}
              />
            </Col>
          )}
        </Spin>
      )}
    </>
  );
};

export default GoodsDetail;
